/**
 * RFC 5545 recurrence rules, built and read back in the small dialect
 * the schedule contract actually uses.
 *
 * Two deliberate boundaries:
 *
 * - The rule NEVER carries `UNTIL`. `TaskScheduleFields` has its own
 *   `until_ms`, and two places to write an end date is two places to
 *   disagree; `COUNT` has no such field, so it stays in the rule.
 * - The rule NEVER carries `DTSTART`. `dtstart_ms` is a contract field,
 *   and the rule body is stored without a prefix (`FREQ=WEEKLY;BYDAY=MO`).
 *
 * Recurrence is wall-clock, expanded server-side in the SCHEDULE's
 * timezone — so every calendar question this file asks (which weekday is
 * this? which day of the month? did that drag cross midnight?) is asked
 * in that zone too, never in the browser's.
 *
 * That distinction is not academic. A `FREQ=WEEKLY;BYDAY=MO` schedule
 * stored in Asia/Singapore, dragged forward four hours from a New York
 * browser, used to be rewritten to `BYDAY=TU` — because 09:00 Monday in
 * Singapore is 21:00 Sunday in New York and +4h crosses New York's
 * midnight. The hotel's Monday clean moved to Tuesdays, permanently, and
 * `describeRule` went on saying "Every Monday" because it formatted with
 * a browser-local `Date` and printed no zone at all.
 *
 * So: every entry point that touches a stored schedule takes an explicit
 * `timeZone`, and it is REQUIRED. There is no browser-local default to
 * fall back into, because falling back into it silently is the whole
 * defect.
 */
import {
  formatZonedClock,
  formatZonedDate,
  isKnownTimeZone,
  timeZoneLabel,
  wallClockOfLocalDate,
  wallClockToEpochMs,
  zonedDayDelta,
  zonedParts,
  zonedWeekday,
} from './zoned-time';

export type RecurrenceFreq = 'DAILY' | 'WEEKLY' | 'MONTHLY';
export type Weekday = 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';
/** `last_weekday`: "the last Friday" from a start on the 22nd-28th that
 *  is its month's last Friday -- the 4th AND the last. `nth_weekday`
 *  from such a start says "the fourth", a different rule in a month with
 *  five; from the 29th-31st it already says "the last". */
export type MonthlyMode = 'day_of_month' | 'nth_weekday' | 'last_weekday';
export type RecurrenceEnd = 'never' | 'on' | 'after';

/** Sunday-first, matching `Date.prototype.getDay()`. */
export const WEEKDAYS: readonly Weekday[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const WEEKDAY_NAME: Record<Weekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

const ORDINAL_NAME: Record<number, string> = {
  1: 'first',
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  [-1]: 'last',
};

/** What the recurrence editor collects. `dtstart` is supplied separately
 *  because the start time is the form's own field, not the rule's. */
export interface RecurrenceSpec {
  freq: RecurrenceFreq;
  /** "every N days/weeks/months"; 1 is omitted from the emitted rule. */
  interval: number;
  /** WEEKLY only. Empty means "the weekday `dtstart` falls on". */
  byDay: Weekday[];
  monthlyMode: MonthlyMode;
  end: RecurrenceEnd;
  /** `end: 'after'` only. */
  count: number;
  /** `end: 'on'` only — a `YYYY-MM-DD` value straight off a date input. */
  untilDate: string;
}

export function defaultRecurrenceSpec(dtstart: Date): RecurrenceSpec {
  return {
    freq: 'WEEKLY',
    interval: 1,
    byDay: [weekdayOf(dtstart)],
    monthlyMode: 'day_of_month',
    end: 'never',
    count: 10,
    untilDate: '',
  };
}

export function weekdayOf(d: Date): Weekday {
  return WEEKDAYS[d.getDay()]!;
}

/** Which occurrence of its weekday within the month `d` is: 1–4, or -1
 *  for the last one (the fifth is always the last). */
export function nthWeekdayOfMonth(d: Date): number {
  const nth = Math.ceil(d.getDate() / 7);
  return nth >= 5 ? -1 : nth;
}

/** Whether `d` is the last of its weekday in its month: no same weekday
 *  a week later in the same month. */
export function isLastWeekdayOfMonth(d: Date): boolean {
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return d.getDate() + 7 > daysInMonth;
}

/**
 * The rule for a task that happens ONCE, at its `dtstart_ms`.
 *
 * A one-shot is a schedule with a single occurrence, not a special kind
 * of row: `COUNT=1` stops the walk after the anchor, so the server
 * expands it to exactly one instant and `rule_produces` answers False
 * for every later one (verified against `scheduler.py`, not assumed).
 * That means one storage shape, one expansion path and one dispatch
 * path for everything on the calendar.
 *
 * The reason this exists at all: a one-shot future task used to be sent
 * to RMF the moment it was created, carrying `earliest_start_ms`. RMF
 * accepted it, bid it out and parked it on a robot -- so a clean booked
 * for Thursday occupied a robot's queue from Monday, showed up as a
 * live queued task, and could not be moved on the calendar because a
 * dispatched task has no reschedule verb. Holding it as a schedule
 * keeps it a calendar entry until its moment arrives.
 *
 * `FREQ=DAILY` is arbitrary -- with `COUNT=1` no frequency is ever
 * reached a second time -- but it must be a frequency `rrulestr`
 * accepts, and DAILY is the one whose expansion is cheapest to bound.
 */
export const ONE_SHOT_RRULE = 'FREQ=DAILY;COUNT=1';

/** True for a rule that produces exactly one occurrence, however it was
 *  written: `COUNT=1` from this module's one-shot path and `COUNT=1`
 *  typed into the recurrence editor's "ends after 1 time" are the same
 *  schedule and must read, drag and delete the same way. */
export function isOneShotRule(rrule: string): boolean {
  return parseRRule(rrule).count === 1;
}

export function buildRRule(spec: RecurrenceSpec, dtstart: Date): string {
  const parts: string[] = [`FREQ=${spec.freq}`];
  const interval = Math.max(1, Math.round(spec.interval || 1));
  if (interval > 1) parts.push(`INTERVAL=${interval}`);

  if (spec.freq === 'WEEKLY') {
    const days = spec.byDay.length > 0 ? sortWeekdays(spec.byDay) : [weekdayOf(dtstart)];
    parts.push(`BYDAY=${days.join(',')}`);
  } else if (spec.freq === 'MONTHLY') {
    if (spec.monthlyMode === 'last_weekday' && isLastWeekdayOfMonth(dtstart)) {
      parts.push(`BYDAY=-1${weekdayOf(dtstart)}`);
    } else if (spec.monthlyMode === 'nth_weekday' || spec.monthlyMode === 'last_weekday') {
      // A "last" the start no longer is: the nth it is, never a -1 seeded
      // on a day the rule would not make.
      parts.push(`BYDAY=${nthWeekdayOfMonth(dtstart)}${weekdayOf(dtstart)}`);
    } else {
      parts.push(`BYMONTHDAY=${dtstart.getDate()}`);
    }
  }

  if (spec.end === 'after') {
    parts.push(`COUNT=${Math.max(1, Math.round(spec.count || 1))}`);
  }
  return parts.join(';');
}

/** The `until_ms` that goes alongside the rule — end of the chosen day
 *  IN THE SITE'S ZONE, so "until 30 Sep" includes the whole of the
 *  hotel's 30th rather than ending it whenever the operator's own
 *  midnight happens to fall. Off by up to a day when the two zones
 *  differ, which is exactly long enough to drop or keep a final clean. */
export function buildUntilMs(spec: RecurrenceSpec, timeZone: string): number | null {
  if (spec.end !== 'on' || !spec.untilDate) return null;
  const [y, m, d] = spec.untilDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  return wallClockToEpochMs(
    { year: y, month: m, day: d, hour: 23, minute: 59, second: 59 },
    timeZone,
  );
}

/**
 * The instant a `datetime-local` value means AT THE SITE.
 *
 * The input hands back a browser-local `Date` whose local fields are the
 * digits the operator typed. Those digits are a wall clock, and the
 * clock they belong to is the building's — "09:00" typed for a Singapore
 * hotel means 09:00 in Singapore no matter where the typing happened.
 * `dtstart.getTime()` reads them as the operator's own clock instead,
 * which is how a London-authored 09:00 became a 16:00 clean.
 */
export function dtstartMsInZone(dtstart: Date, timeZone: string): number {
  return wallClockToEpochMs(wallClockOfLocalDate(dtstart), timeZone);
}

export interface ParsedRule {
  freq: RecurrenceFreq | string;
  interval: number;
  /** Raw BYDAY tokens: `MO`, `2TU`, `-1FR`. */
  byDay: string[];
  byMonthDay: number | null;
  count: number | null;
}

export function parseRRule(rrule: string): ParsedRule {
  const parts = new Map<string, string>();
  for (const chunk of rrule.split(';')) {
    const eq = chunk.indexOf('=');
    if (eq <= 0) continue;
    parts.set(chunk.slice(0, eq).trim().toUpperCase(), chunk.slice(eq + 1).trim());
  }
  const intervalRaw = Number(parts.get('INTERVAL'));
  const countRaw = Number(parts.get('COUNT'));
  const monthDayRaw = Number(parts.get('BYMONTHDAY'));
  return {
    freq: (parts.get('FREQ') ?? '').toUpperCase(),
    interval: Number.isFinite(intervalRaw) && intervalRaw > 0 ? intervalRaw : 1,
    byDay: (parts.get('BYDAY') ?? '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean),
    byMonthDay: Number.isFinite(monthDayRaw) && monthDayRaw !== 0 ? monthDayRaw : null,
    count: Number.isFinite(countRaw) && countRaw > 0 ? countRaw : null,
  };
}

/**
 * Plain language for a stored rule: "Every Monday and Thursday at 09:00
 * Asia/Singapore (+08), until 30 Sep". Written against the PARSED rule
 * rather than the editor spec so the same sentence describes a schedule
 * that came back from the server as describes the one being built.
 *
 * The zone is part of the sentence, not a decoration on it. This string
 * is what an operator reads to CHECK a rule — it is printed in the
 * move/delete confirmation, where the next click can move every future
 * clean — and "09:00" with no zone attached is the one thing that made
 * the whole class of cross-zone defect invisible. Whoever reads it must
 * be able to tell the hotel's morning from their own.
 */
export interface DescribeRuleOptions {
  /** The site's zone, when the screen states it already: a schedule in
   *  that zone is described without it ("Every day at 08:00"). */
  siteTimeZone?: string;
}

const TIME_OF_DAY_PARTS = /(^|;)\s*BY(HOUR|MINUTE|SECOND)\s*=/i;

/** What a rule this console cannot put in words is called, wherever a
 *  rule is described. Never the stored rule text: "FREQ=DAILY;BYHOUR=9"
 *  means nothing to the front desk. */
const CUSTOM_PATTERN = 'A custom pattern';

/** The parts a rule may have and still be put in words with its own
 *  times of day. */
const PLAIN_PARTS = new Set([
  'FREQ',
  'INTERVAL',
  'COUNT',
  'UNTIL',
  'WKST',
  'BYDAY',
  'BYMONTHDAY',
  'BYHOUR',
  'BYMINUTE',
  'BYSECOND',
]);

/**
 * The clocks a rule that picks its own times of day runs at ("09:00",
 * "17:00"), or null when it is not one that can be said simply: a day,
 * week or month rule whose BYHOUR / BYMINUTE are plain numbers, with at
 * most six times a day, and no part beyond those. A part left out takes
 * the start's own (`dtstart` 09:30 with BYHOUR=9,17 runs at 09:30 and
 * 17:30), as the server expands it; seconds are not shown.
 */
function ruleClocks(rrule: string, start: { hour: number; minute: number }): string[] | null {
  const parts = new Map<string, string>();
  for (const chunk of rrule.split(';')) {
    const eq = chunk.indexOf('=');
    if (eq <= 0) continue;
    parts.set(chunk.slice(0, eq).trim().toUpperCase(), chunk.slice(eq + 1).trim());
  }
  const freq = (parts.get('FREQ') ?? '').toUpperCase();
  if (!['DAILY', 'WEEKLY', 'MONTHLY'].includes(freq)) return null;
  if ([...parts.keys()].some((k) => !PLAIN_PARTS.has(k))) return null;
  const numbers = (key: string, max: number, fallback: number): number[] | null => {
    const raw = parts.get(key);
    if (raw === undefined) return [fallback];
    const list = raw.split(',').map((v) => v.trim());
    if (!list.every((v) => /^\d{1,2}$/.test(v) && Number(v) <= max)) return null;
    return [...new Set(list.map(Number))].sort((a, b) => a - b);
  };
  const hours = numbers('BYHOUR', 23, start.hour);
  const minutes = numbers('BYMINUTE', 59, start.minute);
  if (!hours || !minutes || hours.length * minutes.length > 6) return null;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours.flatMap((h) => minutes.map((m) => `${pad(h)}:${pad(m)}`));
}

export function describeRule(
  rrule: string,
  dtstartMs: number,
  untilMs: number | null | undefined,
  timeZone: string,
  opts: DescribeRuleOptions = {},
): string {
  const rule = parseRRule(rrule);
  const start = zonedParts(dtstartMs, timeZone);
  // The zone is dropped only where the screen already says every time on
  // it is the site's (the calendar's "Times in ..." line) AND the
  // schedule expands in that very zone; a schedule in another zone keeps
  // it, which is the case the label exists for.
  const zone =
    opts.siteTimeZone !== undefined && opts.siteTimeZone === timeZone
      ? ''
      : ` ${timeZoneLabel(timeZone, dtstartMs)}`;
  let at = ` at ${formatZonedClock(dtstartMs, timeZone)}${zone}`;

  // A rule that picks its own times of day (BYHOUR / BYMINUTE / BYSECOND,
  // which the editor never writes but the server expands) does not run
  // "at" dtstart's clock. Saying so would name a time it never runs at:
  // its own times are named instead ("Every day at 09:00 and 17:00"),
  // or, when they cannot be said simply, that it is a custom pattern.
  if (TIME_OF_DAY_PARTS.test(rrule)) {
    const clocks = ruleClocks(rrule, start);
    if (clocks === null) return `${CUSTOM_PATTERN}${endClause(rule.count, untilMs, timeZone)}`;
    at = ` at ${joinList(clocks)}${zone}`;
  }
  const every = rule.interval > 1 ? `Every ${rule.interval} ` : 'Every ';

  // A single occurrence is a date, not a pattern. Describing it by its
  // frequency ("Every day ... , 1 times") describes a rule nobody wrote
  // and reads as a repeating clean that will keep coming back -- the
  // opposite of what it does. The frequency in a `COUNT=1` rule is
  // unreachable filler; the date is the whole content.
  if (rule.count === 1) {
    return `Once on ${formatZonedDate(dtstartMs, timeZone)}${at}`;
  }

  let head: string;
  if (rule.freq === 'DAILY') {
    head = rule.interval > 1 ? `${every}days` : 'Every day';
  } else if (rule.freq === 'WEEKLY') {
    const fallback = WEEKDAYS[zonedWeekday(dtstartMs, timeZone)]!;
    const names = (rule.byDay.length ? rule.byDay : [fallback])
      .map(stripNth)
      .map((d) => WEEKDAY_NAME[d as Weekday] ?? d);
    head =
      rule.interval > 1 ? `${every}weeks on ${joinList(names)}` : `Every ${joinList(names)}`;
  } else if (rule.freq === 'MONTHLY') {
    const scope = rule.interval > 1 ? `${every}months` : 'Every month';
    if (rule.byDay.length > 0) {
      const token = rule.byDay[0]!;
      const nth = nthFromToken(token);
      const name = WEEKDAY_NAME[stripNth(token) as Weekday] ?? token;
      head = `${scope} on the ${ORDINAL_NAME[nth] ?? `${nth}th`} ${name}`;
    } else {
      head = `${scope} on day ${rule.byMonthDay ?? start.day}`;
    }
  } else {
    // A rule this editor cannot express (YEARLY, BYSETPOS, ...) is real
    // data somebody stored; say that, and its end, instead of guessing.
    return `${CUSTOM_PATTERN}${endClause(rule.count, untilMs, timeZone)}`;
  }

  return `${head}${at}${endClause(rule.count, untilMs, timeZone)}`;
}

/** The same sentence for a rule still being built in the editor.
 *
 *  `dtstart` here is the operator's TYPED wall clock, not an instant, so
 *  it is converted through the site's zone the same way the submit path
 *  converts it — which is what makes the summary and the schedule that
 *  gets stored say the same thing. */
export function describeRecurrence(
  spec: RecurrenceSpec,
  dtstart: Date,
  timeZone: string,
): string {
  const dtstartMs = dtstartMsInZone(dtstart, timeZone);
  return describeRule(
    buildRRule(spec, dtstart),
    dtstartMs,
    buildUntilMs(spec, timeZone),
    timeZone,
  );
}

export interface ShiftedSeries {
  rrule: string;
  dtstart_ms: number;
}

/**
 * Move a whole series by `deltaMs` — the "All events" branch of a drag.
 *
 * Shifting `dtstart` alone is not enough when the drag crosses midnight:
 * the rule's own day selectors (`BYDAY`, `BYMONTHDAY`) still name the old
 * day, so the series would keep landing on it and only the time would
 * move. Both are rewritten from the new start.
 *
 * "Crosses midnight" is decided on the SCHEDULE's calendar, which is
 * what `timeZone` is for and why it has no default. Deciding it on the
 * browser's rewrote rules in a calendar the schedule does not live in:
 * a Singapore Monday clean, nudged +4h from New York, crossed New
 * York's midnight and came back as `BYDAY=TU` — a permanent, silent
 * one-day shift of the whole series, produced by a gesture the operator
 * read as "half past nine instead of nine".
 */
export function shiftSeries(
  rrule: string,
  dtstartMs: number,
  deltaMs: number,
  timeZone: string,
): ShiftedSeries {
  const newDtstartMs = dtstartMs + deltaMs;
  const dayDelta = zonedDayDelta(dtstartMs, newDtstartMs, timeZone);
  if (dayDelta === 0) return { rrule, dtstart_ms: newDtstartMs };

  const newDayOfMonth = zonedParts(newDtstartMs, timeZone).day;
  const rewritten = rrule
    .split(';')
    .map((chunk) => {
      const eq = chunk.indexOf('=');
      if (eq <= 0) return chunk;
      const key = chunk.slice(0, eq).trim().toUpperCase();
      if (key === 'BYDAY') {
        const shifted = chunk
          .slice(eq + 1)
          .split(',')
          .map((token) => rotateByDayToken(token.trim().toUpperCase(), dayDelta))
          .join(',');
        return `BYDAY=${shifted}`;
      }
      if (key === 'BYMONTHDAY') return `BYMONTHDAY=${newDayOfMonth}`;
      return chunk;
    })
    .join(';');
  return { rrule: rewritten, dtstart_ms: newDtstartMs };
}

/** `2TU` shifted by +1 becomes `2WE`; the ordinal rides along unchanged
 *  because "the second Tuesday" moved by a day is "the second
 *  Wednesday" for every week except one that straddles a month edge —
 *  and RFC 5545 has no way to say that anyway. */
function rotateByDayToken(token: string, dayDelta: number): string {
  const m = /^(-?\d+)?([A-Z]{2})$/.exec(token);
  if (!m) return token;
  const idx = WEEKDAYS.indexOf(m[2] as Weekday);
  if (idx < 0) return token;
  const rotated = WEEKDAYS[(((idx + dayDelta) % 7) + 7) % 7]!;
  return `${m[1] ?? ''}${rotated}`;
}

export interface FollowingRunsPatch extends ShiftedSeries {
  apply_from_ms: number;
}

/**
 * Move one run AND every run after it — "This and following runs" —
 * as the timing PATCH the api-server re-anchors (0.6.54): runs before
 * the cut keep the old rule and their outcomes, the new rule takes over
 * from its first instant on or after the cut, same schedule id.
 *
 * The new rule is seeded at the moved run's NEW instant rather than at
 * the old `dtstart` shifted by the same amount. The server takes the
 * seed's time of day, weekday and day of month as the pattern, and the
 * run being moved is the one instant everybody agrees about: a `dtstart`
 * months back can sit on the other side of a DST change, where the same
 * millisecond shift names a different wall clock. Its day selectors move
 * with it exactly as `shiftSeries` moves them, decided on the
 * SCHEDULE's calendar.
 *
 * `apply_from_ms` is the EARLIER of the two instants, not the run's
 * original start. Dragging the 10:00 run back to 09:00 with the cut at
 * 10:00 would make the first new instant on or after the cut TOMORROW's
 * 09:00: the run being moved would vanish instead of moving. Dragging
 * later, the original start is the earlier one, so the old run is inside
 * the new rule's span and is not left behind as well. The server still
 * lifts the cut to now and past the last recorded run, so a drag can
 * never rewrite a run that already happened.
 *
 * `originalStartMs` is the run's RULE instant (`original_start_ms`), and
 * `newStartMs` where the same rule instant should now be.
 */
export function shiftFollowingRuns(
  rrule: string,
  originalStartMs: number,
  newStartMs: number,
  timeZone: string,
): FollowingRunsPatch {
  const shifted = shiftSeries(rrule, originalStartMs, newStartMs - originalStartMs, timeZone);
  return { ...shifted, apply_from_ms: Math.min(originalStartMs, newStartMs) };
}

// ----------------------------------------------------------------------------
// Reading a stored rule back into the editor
//
// Nothing here may change a schedule the operator did not change. An
// occurrence's identity is the instant the rule produces, so a rule that
// is read into the editor and written back "the same" but is not —
// a dropped `BYHOUR`, a monthly day taken from the wrong date, a start
// that lost its seconds to a `datetime-local` field — silently moves
// every future run, orphans the skips and moves keyed on the old
// instants, and (since 0.6.54) re-anchors the series. So the reading is
// STRICT: a stored rule comes back as an editor spec only when writing
// that spec reproduces it, and otherwise says why it cannot.
// ----------------------------------------------------------------------------

/** Why a stored schedule's timing cannot be shown in the editor. A
 *  clause that completes "its timing can't be changed here because …". */
export interface UnsupportedRule {
  readonly unsupported: string;
}

export function isUnsupportedRule(
  value: RecurrenceSpec | UnsupportedRule,
): value is UnsupportedRule {
  return 'unsupported' in value;
}

/** The keys `buildRRule` writes: the whole of the editor's dialect. */
const EDITOR_RULE_KEYS: ReadonlySet<string> = new Set([
  'FREQ',
  'INTERVAL',
  'BYDAY',
  'BYMONTHDAY',
  'COUNT',
]);

/** RFC 5545 keys the server expands and the editor cannot draw, said in
 *  words for the person whose schedule it is. Anything else unknown gets
 *  the generic clause. */
const FOREIGN_RULE_KEY_REASON: Readonly<Record<string, string>> = {
  BYHOUR: 'it runs at more than one time of day',
  BYMINUTE: 'it runs at more than one time of day',
  BYSECOND: 'it runs at more than one time of day',
  BYSETPOS: 'it picks runs by their position in a set',
  UNTIL: 'its end date is written inside the rule',
  BYMONTH: 'it runs only in some months',
  BYYEARDAY: 'it runs on numbered days of the year',
  BYWEEKNO: 'it runs in numbered weeks of the year',
  WKST: 'it sets its own first day of the week',
};

const BYDAY_TOKEN = /^([+-]?\d+)?(SU|MO|TU|WE|TH|FR|SA)$/;

/** The editor's own bounds (`RecurrenceEditor`'s number inputs clamp to
 *  them), so anything outside would be rewritten by the first keystroke. */
const MAX_INTERVAL = 99;
const MAX_COUNT = 999;

function unsupported(reason: string): UnsupportedRule {
  return { unsupported: reason };
}

/** A whole number in `[min, max]` written as plain digits, else null. */
function boundedWhole(raw: string, min: number, max: number): number | null {
  if (!/^\+?\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n >= min && n <= max ? n : null;
}

/**
 * A stored rule as the editor's spec — the STRICT inverse of
 * `buildRRule` plus `buildUntilMs`.
 *
 * Returns a spec only when `buildRRule(spec, dtstartWallDate(dtstartMs,
 * timeZone))` writes the same rule (`canonicalRule`-equal: FREQ,
 * INTERVAL, BYDAY, BYMONTHDAY and COUNT) and `buildUntilMs(spec,
 * timeZone)` the same `until_ms`, to the second. Anything else is
 * `{ unsupported }` with the reason, rather than the nearest rule the
 * editor can draw: `parseRRule` reads what it recognises and drops the
 * rest, and that leniency is right for a sentence and wrong for a round
 * trip.
 *
 * `timeZone` is the SCHEDULE's own zone (`schedule.timezone`), never the
 * site's: weekday, day of month and the end date are all read on it. A
 * schedule written in another zone (the case the Month sidebar warns
 * about) read on the site's calendar comes back with the wrong days.
 *
 * Seconds in `dtstart_ms` are fine here — the rule does not see them —
 * but see `dtstartFromInput` for why they matter on the way back.
 */
export function specFromRule(
  rrule: string,
  dtstartMs: number,
  untilMs: number | null | undefined,
  timeZone: string,
): RecurrenceSpec | UnsupportedRule {
  if (!isKnownTimeZone(timeZone)) {
    return unsupported(`its time zone (${timeZone}) is not one this browser knows`);
  }
  if (!Number.isFinite(dtstartMs)) return unsupported('it has no start time');

  const body = rrule.trim();
  if (!body || /[\r\n]/.test(body) || /^RRULE:/i.test(body)) {
    return unsupported('it is not stored as a plain repeat rule');
  }
  const parts = new Map<string, string>();
  for (const raw of body.split(';')) {
    const chunk = raw.trim();
    const eq = chunk.indexOf('=');
    if (eq <= 0) return unsupported('it is not stored as a plain repeat rule');
    const key = chunk.slice(0, eq).trim().toUpperCase();
    if (!EDITOR_RULE_KEYS.has(key)) {
      return unsupported(
        FOREIGN_RULE_KEY_REASON[key] ?? `it uses ${key}, which this editor does not write`,
      );
    }
    if (parts.has(key)) return unsupported(`it sets ${key} twice`);
    const value = chunk.slice(eq + 1).trim();
    parts.set(key, value.toUpperCase());
  }

  const freq = parts.get('FREQ');
  if (!freq) return unsupported('it has no repeat frequency');
  if (freq !== 'DAILY' && freq !== 'WEEKLY' && freq !== 'MONTHLY') {
    return unsupported(`it repeats ${freq.toLowerCase()}`);
  }

  const intervalRaw = parts.get('INTERVAL');
  const interval = intervalRaw === undefined ? 1 : boundedWhole(intervalRaw, 1, MAX_INTERVAL);
  if (interval === null) {
    return unsupported(`its interval (${intervalRaw}) is outside the editor's 1–${MAX_INTERVAL}`);
  }
  const countRaw = parts.get('COUNT');
  const count = countRaw === undefined ? null : boundedWhole(countRaw, 1, MAX_COUNT);
  if (countRaw !== undefined && count === null) {
    return unsupported(`its run count (${countRaw}) is outside the editor's 1–${MAX_COUNT}`);
  }
  const hasUntil = untilMs !== null && untilMs !== undefined;
  if (count !== null && hasUntil) {
    return unsupported('it ends both after a number of runs and on a date');
  }

  const wall = dtstartWallDate(dtstartMs, timeZone);
  const startDay = weekdayOf(wall);
  const byDayRaw = parts.get('BYDAY');
  const byMonthDayRaw = parts.get('BYMONTHDAY');
  const tokens = byDayRaw === undefined ? [] : byDayRaw.split(',').map((t) => t.trim());
  if (tokens.some((t) => !BYDAY_TOKEN.test(t))) {
    return unsupported('it names a weekday this editor does not know');
  }

  let byDay: Weekday[] = [startDay];
  let monthlyMode: MonthlyMode = 'day_of_month';
  if (freq === 'DAILY') {
    if (byDayRaw !== undefined) return unsupported('it repeats daily on chosen weekdays only');
    if (byMonthDayRaw !== undefined) return unsupported('it repeats daily on a day of the month');
  } else if (freq === 'WEEKLY') {
    if (byMonthDayRaw !== undefined) return unsupported('it repeats weekly on a day of the month');
    // `buildRRule` always names the days of a weekly rule, so a stored
    // one that does not would come back different — however equivalent.
    if (byDayRaw === undefined) return unsupported('it repeats weekly without naming its days');
    if (tokens.some((t) => /\d/.test(t))) {
      return unsupported('it names a numbered weekday in a weekly rule');
    }
    if (new Set(tokens).size !== tokens.length) return unsupported('it names a weekday twice');
    byDay = sortWeekdays(tokens as Weekday[]);
  } else {
    if (byDayRaw !== undefined && byMonthDayRaw !== undefined) {
      return unsupported('it names both a weekday and a day of the month');
    }
    if (byDayRaw === undefined && byMonthDayRaw === undefined) {
      return unsupported('it repeats monthly without naming the day');
    }
    if (byMonthDayRaw !== undefined) {
      if (byMonthDayRaw.includes(',')) return unsupported('it runs on more than one day a month');
      const day = boundedWhole(byMonthDayRaw, 1, 31);
      if (day === null) {
        return unsupported(
          byMonthDayRaw.startsWith('-')
            ? 'it counts its day from the end of the month'
            : `it runs on day ${byMonthDayRaw} of the month`,
        );
      }
      // `buildRRule` writes the START's day of the month; any other day
      // is a rule the editor cannot draw from this start.
      if (day !== wall.getDate()) {
        return unsupported(
          `it runs on day ${day} of the month but starts on day ${wall.getDate()}`,
        );
      }
    } else {
      if (tokens.length !== 1) return unsupported('it runs on more than one weekday a month');
      const m = BYDAY_TOKEN.exec(tokens[0]!)!;
      if (m[1] === undefined) {
        return unsupported(`it runs on every ${WEEKDAY_NAME[m[2] as Weekday]}`);
      }
      const nth = Number(m[1]);
      // ...and the start's nth weekday, likewise: "the second Tuesday"
      // only round-trips from a start that IS a second Tuesday. "The
      // last Friday" round-trips from any last Friday -- a retime moves
      // the start onto its first new run, often the 22nd-28th.
      if (
        nth === -1 &&
        m[2] === startDay &&
        nthWeekdayOfMonth(wall) !== -1 &&
        isLastWeekdayOfMonth(wall)
      ) {
        monthlyMode = 'last_weekday';
      } else if (nth !== nthWeekdayOfMonth(wall) || m[2] !== startDay) {
        return unsupported(
          `it runs on the ${ORDINAL_NAME[nth] ?? `${nth}th`} ${WEEKDAY_NAME[m[2] as Weekday]} of the month but does not start on one`,
        );
      } else {
        monthlyMode = 'nth_weekday';
      }
    }
  }

  const spec: RecurrenceSpec = {
    freq,
    interval,
    byDay,
    monthlyMode,
    end: count !== null ? 'after' : hasUntil ? 'on' : 'never',
    count: count ?? 10,
    untilDate: hasUntil ? zonedYmd(untilMs, timeZone) : '',
  };
  // To the second: the api-server ends a carry-over (the old time an
  // edit keeps, 0.6.54) at midnight less a millisecond, 23:59:59.999,
  // which is the same day's end. An untouched end goes back exactly as
  // stored either way (`ScheduleEditDrawer`).
  const second = (ms: number) => Math.floor(ms / 1000);
  if (hasUntil && second(buildUntilMs(spec, timeZone)!) !== second(untilMs)) {
    // The editor ends a schedule at 23:59:59 on the chosen day, so an
    // end at any other moment would move the moment it is saved.
    return unsupported(
      `it ends at ${formatZonedClock(untilMs, timeZone)} on ${formatZonedDate(untilMs, timeZone)}, and the editor ends a schedule at the end of a day`,
    );
  }
  // The backstop: everything above is the reasoning, this is the proof.
  if (canonicalRule(buildRRule(spec, wall)) !== canonicalRule(body)) {
    return unsupported('the editor would write it differently');
  }
  return spec;
}

/**
 * A rule in one spelling per meaning, for COMPARING rules — never for
 * storing one.
 *
 * Erases only differences RFC 5545 itself says are none: key order,
 * case, whitespace, the order of a `BYDAY` / `BYMONTHDAY` list, and an
 * explicit `INTERVAL=1`. A weekly rule `shiftSeries` rotated into
 * `BYDAY=TU,TH,SU` and the same rule rebuilt as `BYDAY=SU,TU,TH` are one
 * rule. It does NOT fill in what dtstart implies (a weekly rule naming
 * no days) — that needs the start, and `specFromRule` refuses it.
 */
export function canonicalRule(rrule: string): string {
  const asWritten = rrule.trim().toUpperCase();
  const parts = new Map<string, string>();
  for (const raw of rrule.split(';')) {
    const chunk = raw.trim();
    if (!chunk) continue;
    const eq = chunk.indexOf('=');
    // Not a key=value list at all: nothing to normalise, compare it as
    // written.
    if (eq <= 0) return asWritten;
    const key = chunk.slice(0, eq).trim().toUpperCase();
    if (parts.has(key)) return asWritten;
    const value = chunk.slice(eq + 1).trim();
    parts.set(key, canonicalValue(key, value.toUpperCase()));
  }
  if (parts.get('INTERVAL') === '1') parts.delete('INTERVAL');
  const order = ['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'COUNT'];
  const keys = [
    ...order.filter((k) => parts.has(k)),
    ...[...parts.keys()].filter((k) => !order.includes(k)).sort(),
  ];
  return keys.map((k) => `${k}=${parts.get(k)}`).join(';');
}

function canonicalValue(key: string, value: string): string {
  const items = value.split(',').map((v) => {
    const t = v.trim();
    const day = BYDAY_TOKEN.exec(t);
    if (day) return `${day[1] !== undefined ? Number(day[1]) : ''}${day[2]}`;
    return /^[+-]?\d+$/.test(t) ? String(Number(t)) : t;
  });
  if (key === 'BYDAY') {
    items.sort((a, b) => {
      const da = WEEKDAYS.indexOf(stripNth(a) as Weekday);
      const db = WEEKDAYS.indexOf(stripNth(b) as Weekday);
      return da !== db ? da - db : nthFromToken(a) - nthFromToken(b);
    });
  } else if (key.startsWith('BY') && items.every((i) => /^-?\d+$/.test(i))) {
    items.sort((a, b) => Number(a) - Number(b));
  }
  return items.join(',');
}

/** The three fields that decide WHEN a schedule runs. Any `TaskSchedule`
 *  is one. */
export interface ScheduleTiming {
  readonly rrule: string;
  readonly dtstart_ms: number;
  readonly timezone: string;
}

/**
 * Whether `after` runs at different times from `before` — the question
 * the api-server asks to decide between an in-place edit and a
 * re-anchor (0.6.54), and the one an editor must ask before it sends
 * `rrule`, `dtstart_ms` or `timezone` at all.
 *
 * When this is false send NONE of the three. The rule is compared in
 * `canonicalRule` form, so a rule rebuilt from an untouched editor in a
 * different spelling is not a change here; the server may compare the
 * text, and sending that spelling would re-anchor a schedule nobody
 * re-timed. When it is true send all three, with `apply_from_ms`.
 */
export function timingChanged(before: ScheduleTiming, after: ScheduleTiming): boolean {
  return (
    before.dtstart_ms !== after.dtstart_ms ||
    before.timezone !== after.timezone ||
    canonicalRule(before.rrule) !== canonicalRule(after.rrule)
  );
}

/**
 * A `Date` whose BROWSER-LOCAL fields are the wall clock `timeZone`
 * shows at `dtstartMs` — the shape `buildRRule`, `RecurrenceEditor` and
 * `describeRecurrence` take as `dtstart`, which is otherwise the parsed
 * `datetime-local` value the operator typed. For a STORED schedule,
 * with the schedule's own zone.
 */
export function dtstartWallDate(dtstartMs: number, timeZone: string): Date {
  const { year, month, day, hour, minute, second } = zonedParts(dtstartMs, timeZone);
  return new Date(year, month - 1, day, hour, minute, second);
}

/** The `datetime-local` value (`YYYY-MM-DDTHH:mm`) for a stored start, on
 *  the schedule's clock. Minute precision: all the input can hold. */
export function dtstartInputValue(dtstartMs: number, timeZone: string): string {
  const p = zonedParts(dtstartMs, timeZone);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/**
 * The `dtstart_ms` to save from the editor's start field: the STORED
 * instant, untouched, unless the operator changed the field.
 *
 * `datetime-local` holds minutes, so a start stored at 09:00:30 reads
 * back as 09:00 and re-deriving it would write 09:00:00 — a different
 * `dtstart_ms`, a timing change to the server, and every future run's
 * identity moved by thirty seconds, for an edit that only renamed the
 * schedule. Equality with `dtstartInputValue` is what "unchanged"
 * means here. Null for a value that does not parse.
 *
 * A changed value is read off its DIGITS, not through `new Date(value)`
 * as the create form's `dtstartMsInZone` does: that resolves the digits
 * on the browser's clock first, so a time inside the BROWSER's own
 * spring-forward gap (02:30 on the night New York skips it, typed there
 * for a Singapore schedule) comes back an hour later before the
 * schedule's zone is ever consulted.
 */
export function dtstartFromInput(value: string, storedMs: number, timeZone: string): number | null {
  if (value === dtstartInputValue(storedMs, timeZone)) return storedMs;
  const m = INPUT_WALL_CLOCK.exec(value);
  if (!m) return null;
  const [year, month, day, hour, minute, second] = m.slice(1).map((v) => Number(v ?? 0)) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  // A calendar date that does not exist (30 Feb) is refused rather than
  // rolled into the next month.
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  if (hour > 23 || minute > 59 || second > 59) return null;
  return wallClockToEpochMs({ year, month, day, hour, minute, second }, timeZone);
}

/** What a `datetime-local` input hands back: `YYYY-MM-DDTHH:mm`, with
 *  seconds when the input's `step` allows them. */
const INPUT_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** `YYYY-MM-DD` on `timeZone`'s calendar: what the end-date field holds. */
function zonedYmd(epochMs: number, timeZone: string): string {
  const { year, month, day } = zonedParts(epochMs, timeZone);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** `formatDate`, not `toLocaleDateString`: the end of a recurrence is
 *  exactly the kind of date `utils/date-format.ts` exists to keep
 *  unambiguous, and a locale-dependent "Sep 30" / "30 Sep" split in a
 *  sentence the operator reads to CHECK the rule is worse than
 *  anywhere else in the SPA. */
function endClause(
  count: number | null,
  untilMs: number | null | undefined,
  timeZone: string,
): string {
  if (count) return `, ${count} times`;
  // On the SCHEDULE's calendar. `until_ms` is the end of the site's
  // chosen day, which for most of the hours in a cross-zone schedule
  // falls on the operator's NEXT day — "until 30 Sep" rendering as
  // 01/Oct is a reader losing confidence in a rule that is correct.
  if (untilMs) return `, until ${formatZonedDate(untilMs, timeZone)}`;
  return '';
}

function stripNth(token: string): string {
  return token.replace(/^-?\d+/, '');
}

function nthFromToken(token: string): number {
  const m = /^(-?\d+)/.exec(token);
  return m ? Number(m[1]) : 1;
}

function sortWeekdays(days: Weekday[]): Weekday[] {
  const seen = new Set(days);
  return WEEKDAYS.filter((d) => seen.has(d));
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

